/**
 * 我的与设备（与 iOS Apps/Parent/Sources/Me/MeView.swift 一致，设计稿 20）：
 * 个人资料与家庭成员（邀请家人）、孩子列表（编辑 / 添加）、学生设备（在线状态、解绑、绑定新平板，安卓平板点开管理 App）、语音助手设置、MCP 接入、退出登录。
 * 地址参数：?add=child 打开"添加孩子"，?bind=<绑定码> 直接进入确认绑定。
 * 调试：?dev_sheet=invite / bind / child_edit / child_new / device_apps（第一台安卓平板上的 App），?dev_bind=<绑定码>。
 */
import { ChevronRight, Plug, QrCode, Smartphone, Tablet } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ago, api, confirm, devParam, isAndroidTablet, logout, presenceOf, refreshDevices, refreshFamily, run, select, shallowEqual, takeDevSheet, useChildren, useCurrentChild, useMyRole, useStore, type Child, type Device } from "../../core";
import { Avatar, Button, C, categoryStyle, OnlineDot, Page, PageTitle, RowDivider } from "../../ui";
import { BindSheet } from "./BindSheet";
import { ChildEditSheet } from "./ChildEditSheet";
import { DeviceAppsSheet } from "./DeviceAppsSheet";
import { InviteSheet } from "./InviteSheet";
import { gradeName } from "./util";
import "./me.css";

export { McpPage } from "./mcp";

/** 编辑中的孩子：null 表示新建 */
type ChildTarget = { child: Child | null };

export function MePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const current = useCurrentChild();
  const [editing, setEditing] = useState<ChildTarget | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [bind, setBind] = useState<{ code: string | null } | null>(null);

  useEffect(() => {
    void refreshDevices();
    void refreshFamily();
  }, []);

  // 地址参数和调试参数（每次加载只处理一次）
  useEffect(() => {
    if (takeDevSheet("invite")) setShowInvite(true);
    if (takeDevSheet("child_new")) setEditing({ child: null });
    if (takeDevSheet("child_edit") && current) setEditing({ child: current });
    const devBind = devParam("dev_bind");
    if (takeDevSheet("bind") || devBind) setBind({ code: devBind });
  }, []);

  useEffect(() => {
    const add = params.get("add");
    const code = params.get("bind");
    if (!add && !code) return;
    if (add === "child") setEditing({ child: null });
    if (code) setBind({ code });
    setParams({}, { replace: true });
  }, [params, setParams]);

  return (
    <Page gap={12}>
      <PageTitle>我的</PageTitle>
      <div className="me-grid">
        <div className="me-col">
          <ProfileCard onInvite={() => setShowInvite(true)} />
          <ChildrenCard onEdit={(c) => setEditing({ child: c })} />
          <div className="me-card me-links">
            <button type="button" className="me-link" onClick={() => navigate("/assistant/settings")}>
              <span>语音助手设置</span>
              <span className="me-link-value">
                API Key、孩子的小助手
                <ChevronRight size={16} strokeWidth={2.4} />
              </span>
            </button>
            <RowDivider />
            <button type="button" className="me-link" onClick={() => navigate("/me/mcp")}>
              <span>MCP 接入</span>
              <span className="me-link-value">
                <Plug size={15} />
                接入 Claude 等 AI 助手
                <ChevronRight size={16} strokeWidth={2.4} />
              </span>
            </button>
          </div>
        </div>
        <div className="me-col">
          <DevicesCard onBind={() => setBind({ code: null })} />
          <button
            type="button"
            className="me-card me-logout"
            onClick={async () => {
              if (await confirm({ title: "退出登录？", message: "会清除这个浏览器里保存的登录信息和缓存数据。", confirmText: "退出登录", danger: true })) await logout();
            }}
          >
            退出登录
          </button>
          <p className="me-footer">服务器：{location.origin} · 网页版</p>
        </div>
      </div>

      <ChildEditSheet open={editing != null} child={editing?.child ?? null} onClose={() => setEditing(null)} />
      <InviteSheet open={showInvite} onClose={() => setShowInvite(false)} />
      <BindSheet open={bind != null} initialCode={bind?.code} onClose={() => setBind(null)} />
    </Page>
  );
}

function ProfileCard({ onInvite }: { onInvite: () => void }) {
  const me = useStore((s) => s.me);
  const members = useStore((s) => s.members);
  const role = useMyRole();
  const name = me?.displayName || "家长";
  const course = categoryStyle("course");
  return (
    <div className="me-card me-profile">
      <Avatar name={Array.from(name).slice(-1)[0] ?? "家"} size={48} tint={course.tint} ink={course.ink} />
      <div className="me-profile-text">
        <strong>
          {name}
          {role === "owner" ? " · 管理员" : ""}
        </strong>
        <span>{me?.email ?? ""}</span>
        {members.length > 0 && (
          <small>
            家庭成员 {members.length} 人：{members.map((m) => m.displayName + (m.role === "owner" ? "（管理员）" : "")).join("、")}
          </small>
        )}
      </div>
      <Button variant="soft" height={36} className="me-small-btn" onClick={onInvite}>
        邀请家人
      </Button>
    </div>
  );
}

function quietText(c: Child): string {
  const s = c.settings?.quietStart;
  const e = c.settings?.quietEnd;
  if (s && e) return `免打扰 ${s}–${e}`;
  return c.grade ? gradeName(c.grade) : "";
}

function ChildrenCard({ onEdit }: { onEdit: (c: Child | null) => void }) {
  const children = useChildren();
  return (
    <div className="me-card">
      <h2 className="me-card-title">孩子</h2>
      {children.map((c) => (
        <div key={c.id}>
          <RowDivider />
          <button type="button" className="me-child" onClick={() => onEdit(c)}>
            <Avatar name={c.name} size={36} />
            <strong>{c.name}</strong>
            <span>{quietText(c)}</span>
            <ChevronRight size={16} strokeWidth={2.4} color={C.inkMuted} />
          </button>
        </div>
      ))}
      <RowDivider />
      <button type="button" className="me-add" onClick={() => onEdit(null)}>
        + 添加孩子
      </button>
    </div>
  );
}

function DevicesCard({ onBind }: { onBind: () => void }) {
  const devices = useStore((s) => select.studentDevices(s), shallowEqual);
  const onlineIds = useStore((s) => s.onlineDeviceIds);
  const children = useStore((s) => s.children);
  // 查看 App 的平板（只有安卓平板支持）
  const [appsOf, setAppsOf] = useState<Device | null>(null);

  // 调试：?dev_sheet=device_apps（设备列表读到后打开第一台安卓平板）
  useEffect(() => {
    const t = devices.find(isAndroidTablet);
    if (t && takeDevSheet("device_apps")) setAppsOf(t);
  }, [devices]);

  async function revoke(d: Device) {
    const ok = await confirm({ title: `解绑「${d.name}」？`, message: "解绑后这台平板会清空数据，回到扫码绑定页。", confirmText: "解绑", danger: true });
    if (!ok) return;
    const r = await run(() => api.del(`devices/${d.id}`), "已解绑");
    if (r !== undefined) await refreshDevices();
  }

  return (
    <div className="me-card">
      <h2 className="me-card-title">学生设备</h2>
      {devices.length === 0 && (
        <>
          <RowDivider />
          <p className="me-empty">还没有绑定平板</p>
        </>
      )}
      {devices.map((d) => {
        const level = presenceOf(d, onlineIds);
        const label = level === "online" ? "在线" : level === "standby" ? "待机" : "离线";
        const synced = d.lastSeenAt != null ? `${ago(d.lastSeenAt)}同步` : "从未同步";
        const Icon = d.model?.toLowerCase().includes("iphone") || d.model?.toLowerCase().includes("phone") ? Smartphone : Tablet;
        const manageable = isAndroidTablet(d);
        const main = (
          <>
            <span className="me-device-icon">
              <Icon size={20} />
            </span>
            <div className="me-device-text">
              <strong>{d.name}</strong>
              <OnlineDot level={level} text={`${label} · ${children[d.childId ?? ""]?.name ?? ""} · ${synced}`} />
            </div>
          </>
        );
        return (
          <div key={d.id}>
            <RowDivider />
            <div className="me-device">
              {manageable ? (
                <button type="button" className="me-device-main" onClick={() => setAppsOf(d)} aria-label={`${d.name}：App 管理`}>
                  {main}
                  <span className="me-device-apps">
                    App 管理
                    <ChevronRight size={16} strokeWidth={2.4} />
                  </span>
                </button>
              ) : (
                main
              )}
              <button type="button" className="me-revoke" onClick={() => void revoke(d)}>
                解绑
              </button>
            </div>
          </div>
        );
      })}
      <Button variant="dark" block height={48} icon={<QrCode size={18} />} onClick={onBind} className="me-bind-btn">
        绑定新平板
      </Button>
      <DeviceAppsSheet device={appsOf} onClose={() => setAppsOf(null)} />
    </div>
  );
}
